import { useMakeSelector } from 'hooks/use-make-selector';
import { usePodClickTracking } from 'hooks/use-pod-click-tracking';
import useTrolleyActions from 'hooks/use-trolley-actions';
import { useWtrDispatch, useWtrSelector } from 'redux/hooks';
import { makeGetIsFavourite } from 'redux/modules/favourites-products/selectors';
import { addRemoveFavourite } from 'redux/modules/search-and-browse/actions/add-remove-favourite';
import { makeGetIsRedPriceProduct } from 'redux/modules/entities/selectors/promotions';
import { getPropsFromDataset } from 'redux/middleware/analytics/actions-to-track/trolley/utils/get-props-from-dataset';
import { getPageType } from 'utils/get-page-type';
import { getLocation } from 'redux/modules/routing/selectors';
import { makeGetDSConflict } from './selectors/selectConflict';
import { makeGetDSProduct } from './selectors/selectProduct';
import { updateTrolleyParams } from './utils';

// this returns object that conforms to props type of Design System (DS) pod
// @johnlewispartnership/wtr-ingredients/ingredients/ProductPod

export function useDSPodProps({ lineNumber, position, sponsored, clickContext, linkTarget }) {
  const dispatch = useWtrDispatch();
  const wrapperId = getPageType(useWtrSelector(getLocation)?.pathname);
  const isFavourite = useMakeSelector(makeGetIsFavourite, lineNumber);
  const { product, productData } = useMakeSelector(makeGetDSProduct, lineNumber);

  const redOfferText = useMakeSelector(makeGetIsRedPriceProduct, lineNumber);
  const {
    addToTrolley,
    updateInTrolley,
    optimisticTrolleyQuantityAmount: amount,
    optimisticTrolleyQuantityUom: uomId = product.uoms.item,
  } = useTrolleyActions({ lineNumber, productId: product.productId });
  const conflict = useMakeSelector(makeGetDSConflict, product.productId, amount > 0, {
    isFavourite,
    position,
    sponsored,
    wrapperId,
    boosted: false,
  });
  const {
    reportClickToAdTech,
    reportAnalyticsClick,
    reportClickToDataLayer,
    reportClickToMonetate,
  } = usePodClickTracking({
    conflictMessage: conflict?.message?.toLowerCase(),
    isFavourite,
    position,
    productId: product.productId,
    wrapperId,
    boosted: false,
    sponsored,
    listDefinitions: clickContext.listDefinitions,
  });

  // conform to type @johnlewispartnership/wtr-ingredients/ingredients/ProductPod/types.ts#Product
  // eslint-disable-next-line react-hooks/immutability
  product.sponsored = sponsored;
  // eslint-disable-next-line react-hooks/immutability
  product.favourite = isFavourite;
  // eslint-disable-next-line react-hooks/immutability
  product.productLinkProps.onClick = reportAnalyticsClick;
  // eslint-disable-next-line react-hooks/immutability
  product.productLinkProps.target = linkTarget;
  // eslint-disable-next-line react-hooks/immutability
  product.promotions = product.promotions.map(promotion => ({
    ...promotion,
    linkProps: { ...promotion.linkProps, target: linkTarget },
  }));

  const trolleyCtx = e => ({
    productId: product.productId,
    lineNumber,
    position,
    clickContext: {
      ...clickContext,
      ...getPropsFromDataset(e?.currentTarget.closest('article')),
    },
  });

  // handlers
  const onAddToTrolley = (q, e) => {
    addToTrolley(updateTrolleyParams(q, e, trolleyCtx(e)));
  };
  const onUpdateTrolleyQuantity = (q, e) => {
    updateInTrolley(updateTrolleyParams(q, e, trolleyCtx(e), 'newQuantity'));
    reportClickToAdTech();
  };

  const onFavouriteClick = () => {
    if (!isFavourite) reportClickToAdTech();
    reportClickToDataLayer();
    reportClickToMonetate();
    dispatch(addRemoveFavourite(isFavourite, lineNumber));
  };

  if (conflict?.resolution?.type === 'button') {
    // eslint-disable-next-line react-hooks/immutability
    conflict.resolution.action.onClick = e => onUpdateTrolleyQuantity({ amount: 0, uomId }, e);
  }

  return {
    product,
    conflict,
    trolleyQuantity: { amount, uomId },
    onFavouriteClick,
    onAddToTrolley,
    onUpdateTrolleyQuantity,
    productData,
    redOfferText,
  };
}
